import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import moment from 'moment';

const fmt = (date, format = 'MMM D, YYYY') => date ? moment(date).format(format) : 'Not provided';
const money = (value) => {
	if (value === null || value === undefined || value === '') return 'Not provided';
	const parsed = Number(value);
	if (Number.isNaN(parsed)) return String(value);
	return `$${parsed.toLocaleString()}`;
};

const styles = StyleSheet.create({
	page: {
		padding: 36,
		fontSize: 10,
		fontFamily: 'Helvetica',
		color: '#1a1a1a'
	},
	header: {
		marginBottom: 14,
		borderBottom: '1 solid #e5e7eb',
		paddingBottom: 10
	},
	title: {
		fontSize: 18,
		fontFamily: 'Helvetica-Bold',
		marginBottom: 2
	},
	subtitle: {
		fontSize: 10,
		color: '#6b7280'
	},
	runningHeader: {
		position: 'absolute',
		top: 20,
		left: 36,
		right: 36,
		fontSize: 9,
		color: '#6b7280',
		paddingBottom: 4,
		borderBottom: '1 solid #e5e7eb'
	},
	section: {
		marginTop: 12,
		border: '1 solid #e5e7eb',
		borderRadius: 6
	},
	sectionTitle: {
		fontSize: 11,
		fontFamily: 'Helvetica-Bold',
		backgroundColor: '#f9fafb',
		padding: '7 10',
		borderBottom: '1 solid #e5e7eb'
	},
	sectionBody: {
		padding: '8 10'
	},
	row: {
		flexDirection: 'row',
		paddingVertical: 4,
		borderBottom: '1 solid #f3f4f6'
	},
	lastRow: {
		borderBottom: 'none'
	},
	label: {
		width: '40%',
		fontFamily: 'Helvetica-Bold',
		fontSize: 9,
		color: '#4b5563'
	},
	value: {
		width: '60%',
		fontSize: 9,
		color: '#111827'
	},
	empty: {
		fontSize: 9,
		color: '#9ca3af',
		fontStyle: 'italic'
	},
	periodCard: {
		marginTop: 8,
		padding: 8,
		border: '1 solid #e5e7eb',
		borderRadius: 6
	},
	periodTitle: {
		fontSize: 10,
		fontFamily: 'Helvetica-Bold',
		marginBottom: 4
	},
	roomTypeLine: {
		fontSize: 8,
		color: '#374151',
		paddingVertical: 1
	},
	footer: {
		marginTop: 14,
		fontSize: 8,
		color: '#9ca3af'
	}
});

const Row = ({ label, value, isLast = false }) => (
	<View style={[styles.row, isLast ? styles.lastRow : null]}>
		<Text style={styles.label}>{label}</Text>
		<Text style={styles.value}>{value || 'Not provided'}</Text>
	</View>
);

const VenueDetailPDF = ({ venue = {}, edited = {} }) => {
	const peakPeriods = edited.peakPeriods || [];
	const venueName = venue.name || 'Venue';
	const today = moment().format('MMMM D, YYYY');

	return (
		<Document title={`${venueName} - Proposal & Sourcing`}>
			<Page size="LETTER" style={styles.page}>
				<Text
					fixed
					style={styles.runningHeader}
					render={({ pageNumber }) => (
						pageNumber > 1 ? `${venueName} - Proposal & Sourcing Details` : ''
					)}
				/>
				<View style={styles.header}>
					<Text style={styles.title}>{venueName}</Text>
					<Text style={styles.subtitle}>Proposal & Sourcing Details</Text>
				</View>

				<View style={styles.section}>
					<Text style={styles.sectionTitle}>Proposal</Text>
					<View style={styles.sectionBody}>
						<Row label="Status" value={edited.status || 'Draft'} />
						<Row label="Date Sent" value={fmt(edited.sentDate)} />
						<Row label="Response Date" value={fmt(edited.responseDate)} />
						<Row label="Contracted Date" value={fmt(edited.contractedDate)} />
						<Row label="Quoted Room Rate" value={money(edited.quotedRoomRate)} />
						<Row label="Meeting Space Cost" value={money(edited.quotedMeetingSpaceCost)} />
						<Row label="F&B Minimum" value={money(edited.quotedFBMinimum)} />
						<Row label="Contract Value" value={money(edited.contractValue)} />
						<Row label="Additional Details" value={edited.additionalDetails || 'Not provided'} isLast />
					</View>
				</View>

				<View style={styles.section}>
					<Text style={styles.sectionTitle}>Sourcing Details</Text>
					<View style={styles.sectionBody}>
						<Row label="Hotel Website" value={edited.hotelWebsite || 'Not provided'} />
						<Row label="Total Guest Rooms" value={edited.totalGuestRooms ? String(edited.totalGuestRooms) : 'Not provided'} />
						<Row label="Rooms & Space on Hold" value={edited.roomsOnHold || 'Not provided'} />
						<Row label="Proposal Expiration Date" value={fmt(edited.proposalExpirationDate)} />
						<Row label="Room Tax / Mandatory Fees" value={edited.roomTaxFees || 'Not provided'} />
						<Row label="Function Space Rental" value={money(edited.functionSpaceRental)} />
						<Row label="Meeting Room Assignments" value={edited.meetingRoomAssignments || 'Not provided'} />
						<Row label="Office Space Assignments" value={edited.officeSpaceAssignments || 'Not provided'} />
						<Row label="F&B Functions Assignments" value={edited.fbFunctionsAssignments || 'Not provided'} />
						<Row label="Other Space" value={edited.otherSpace || 'Not provided'} />
						<Row label="Distance to Restaurants & Bars" value={edited.distanceToRestaurants || 'Not provided'} />
						<Row label="Airport Distance" value={edited.airportDistance || 'Not provided'} />
						<Row label="Recent Renovations" value={edited.recentRenovations || 'Not provided'} />
						<Row label="Concessions" value={edited.concessions || 'Not provided'} isLast={peakPeriods.length === 0} />

						{peakPeriods.length > 0 ? (
							<View style={{ marginTop: 6 }} break>
								<Text style={styles.periodTitle}>Peak Periods</Text>
								{peakPeriods.map((period, idx) => (
									<View key={idx} style={styles.periodCard} wrap={false}>
										<Text style={styles.periodTitle}>Period {idx + 1}</Text>
										<Text style={styles.roomTypeLine}>Arrival: {fmt(period.arrivalDate)}</Text>
										<Text style={styles.roomTypeLine}>Departure: {fmt(period.departureDate)}</Text>
										<Text style={styles.roomTypeLine}>Suite Rate: {money(period.suiteRate)}</Text>
										<Text style={styles.roomTypeLine}>Staff Rate: {money(period.staffRate)}</Text>
										<Text style={styles.roomTypeLine}>Resort/Destination Fee: {period.resortFee || 'Not provided'}</Text>
										{(period.roomTypes || []).length > 0 ? (
											(period.roomTypes || []).map((roomType, roomIdx) => (
												<Text key={roomIdx} style={styles.roomTypeLine}>
													- {roomType.type || 'Room type'} | Rate: {money(roomType.rate)} | Count: {roomType.count || '0'}
												</Text>
											))
										) : (
											<Text style={styles.empty}>No room types added.</Text>
										)}
									</View>
								))}
							</View>
						) : null}
					</View>
				</View>

				<Text style={styles.footer}>Generated on {today}</Text>
			</Page>
		</Document>
	);
};

export default VenueDetailPDF;
